/**
 * Hooks do módulo Atividades (Fase 3 — item 3.3)
 *
 * Wrappers TanStack Query para as RPCs:
 *  - list_user_activities_paginated  -> useActivitiesPaginated
 *  - get_activities_kpis             -> useActivitiesKpis
 *
 * Mantemos um único arquivo para evitar duplicação de tipos de filtros, já
 * que ambos os hooks compartilham o mesmo conjunto de filtros (exceto
 * paginação/ordenação que são exclusivos do listador).
 *
 * @version 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// =============================================================================
// Tipos compartilhados
// =============================================================================

/** Origem da atividade na listagem unificada. */
// v1.4.0: 'booking' representa agendamentos online (scheduling_bookings)
export type ActivityOrigin = 'standalone' | 'cadence' | 'booking';

/** Status normalizado retornado pela RPC. */
export type ActivityStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'skipped'
  | 'overdue';

/**
 * Filtros aceitos por ambas as RPCs.
 * Datas em formato ISO 'YYYY-MM-DD' (DATE no Postgres).
 */
export interface ActivitiesFilters {
  dateStart?: string | null;
  dateEnd?: string | null;
  assigneeIds?: string[] | null;
  activityTypes?: string[] | null;
  status?: ActivityStatus[] | null;
  pipelineIds?: string[] | null;
  phaseIds?: string[] | null;
  playbookIds?: string[] | null;
  origin?: ActivityOrigin | null;
  search?: string | null;
  /** v1.2.0 — filtros novos espelhando a listagem de Negócios */
  workspaceIds?: string[] | null;
  categoryIds?: string[] | null;
  productIds?: string[] | null;
  sources?: string[] | null;
  /** v1.3.0 — filtra por sort_order do step da cadência (drill-down do funil) */
  stepSortOrders?: number[] | null;
  /** v1.4.0 — filtra atividades pelo status do negócio (won/lost/open) */
  dealStatus?: 'won' | 'lost' | 'open' | null;
  /** v1.5.0 — filtra pelo executor real (Realizado por). Array de crm_sellers.id */
  performedByIds?: string[] | null;
  /** v1.6.0 — filtros por data de criação da atividade (YYYY-MM-DD) */
  createdStart?: string | null;
  createdEnd?: string | null;
  /** v1.6.0 — filtros por data de conclusão da atividade (YYYY-MM-DD) */
  completedStart?: string | null;
  completedEnd?: string | null;
  /** Timezone IANA usado para comparar datas (default São Paulo). */
  timezone?: string;
}


/** Linha retornada pela listagem (espelha o RETURNS TABLE da RPC). */
export interface ActivityRow {
  activity_id: string;
  origin: ActivityOrigin;
  activity_type: string;
  title: string;
  description: string | null;
  status: ActivityStatus;
  due_at: string | null;
  completed_at: string | null;
  is_overdue: boolean;
  deal_id: string | null;
  deal_title: string | null;
  /** v1.1.0 — valor do negócio para exibição na listagem */
  deal_value: number | null;
  /** v1.1.0 — moeda do negócio (ex: BRL) */
  deal_currency: string | null;
  /** v1.2.0 — status derivado da fase atual (won/lost/open) */
  deal_status: 'open' | 'won' | 'lost';
  pipeline_id: string | null;
  pipeline_name: string | null;
  phase_id: string | null;
  phase_name: string | null;
  /** v1.2.0 — cor da fase (para barra de progresso) */
  phase_color: string | null;
  /** v1.2.0 — posição da fase no pipeline (1-indexed) */
  phase_sort_order: number | null;
  /** v1.2.0 — workspace do pipeline */
  workspace_id: string | null;
  workspace_name: string | null;
  customer_id: string | null;
  /** v1.2.0 — Resolvido server-side: cliente -> contato -> company_name */
  customer_name: string | null;
  /** v1.2.0 — telefone do contato vinculado ao deal (raw) */
  customer_phone: string | null;
  /** v1.15.0 — avatar do contato vinculado (coluna Cliente padronizada) */
  customer_avatar: string | null;
  /** v1.2.0 — origem do negócio (lead source) */
  source: string | null;
  assignee_id: string | null;
  /** v1.1.0 — nome do responsável (vendedor do negócio) */
  assignee_name: string | null;
  /** v1.1.0 — avatar URL do responsável */
  assignee_avatar: string | null;
  cadence_id: string | null;
  playbook_id: string | null;
  playbook_name: string | null;
  step_sort_order: number | null;
  is_required: boolean;
  /** v1.5.0 — "Realizado por": executor real da atividade (NULL se não houver). */
  performed_by_id: string | null;
  performed_by_name: string | null;
  performed_by_avatar: string | null;
  /** v1.6.0 — data de criação da atividade (ISO) */
  created_at: string | null;
  /**
   * v1.13.0 — TRUE quando o step de cadência foi criado manualmente pelo
   * vendedor (permite exibir botão "Editar" na listagem). Sempre FALSE para
   * atividades avulsas (standalone) e agendamentos (booking).
   */
  is_manual: boolean;
  total_count: number;
}


/** KPIs retornados (linha única). */
export interface ActivitiesKpis {
  pending_today: number;
  overdue: number;
  completed_in_period: number;
  /** v1.1.0 — atividades puladas no período */
  skipped_in_period: number;
  total_in_period: number;
  /** v1.2.0 — pendentes (pending/in_progress) dentro do período filtrado */
  pending_in_period: number;
  completion_rate: number;
}

// =============================================================================
// useActivitiesPaginated — listagem paginada
// =============================================================================

export interface UseActivitiesPaginatedParams extends ActivitiesFilters {
  page?: number;
  pageSize?: number;
  /** Coluna whitelisted no backend: due_at | created_at | title | status | deal_title */
  orderBy?: 'due_at' | 'created_at' | 'title' | 'status' | 'deal_title';
  orderDir?: 'asc' | 'desc';
  enabled?: boolean;
}

/**
 * Lista paginada de atividades (avulsas + cadência) já normalizadas.
 *
 * Retorna `rows` (página atual), `total` (count global filtrado) e flags
 * úteis de paginação calculadas no client.
 */
export function useActivitiesPaginated(params: UseActivitiesPaginatedParams = {}) {
  const {
    page = 1,
    pageSize = 25,
    orderBy = 'due_at',
    orderDir = 'asc',
    timezone = 'America/Sao_Paulo',
    enabled = true,
    ...filters
  } = params;

  return useQuery({
    queryKey: ['activities-paginated', { page, pageSize, orderBy, orderDir, timezone, ...filters }],
    enabled,
    queryFn: async () => {
      // Chamada da RPC unificada — backend já valida tenant via get_validated_tenant_id
      const { data, error } = await supabase.rpc('list_user_activities_paginated', {
        p_page: page,
        p_page_size: pageSize,
        p_date_start: filters.dateStart ?? null,
        p_date_end: filters.dateEnd ?? null,
        p_assignee_ids: filters.assigneeIds ?? null,
        p_activity_types: filters.activityTypes ?? null,
        p_status: filters.status ?? null,
        p_pipeline_ids: filters.pipelineIds ?? null,
        p_phase_ids: filters.phaseIds ?? null,
        p_playbook_ids: filters.playbookIds ?? null,
        p_origin: filters.origin ?? null,
        p_search: filters.search ?? null,
        p_order_by: orderBy,
        p_order_dir: orderDir,
        p_timezone: timezone,
        // v1.2.0 — filtros novos espelhando a listagem de Negócios
        p_workspace_ids: filters.workspaceIds ?? null,
        p_category_ids: filters.categoryIds ?? null,
        p_product_ids: filters.productIds ?? null,
        p_sources: filters.sources ?? null,
        // v1.3.0 — drill-down por step da cadência
        p_step_sort_orders: filters.stepSortOrders ?? null,
        // v1.4.0 — filtro por status do negócio (won/lost/open)
        p_deal_status: filters.dealStatus ?? null,
        // v1.5.0 — filtro por executor real (Realizado por)
        p_performed_by_ids: filters.performedByIds ?? null,
        // v1.6.0 — filtros aditivos por data de criação / conclusão
        p_created_start: filters.createdStart ?? null,
        p_created_end: filters.createdEnd ?? null,
        p_completed_start: filters.completedStart ?? null,
        p_completed_end: filters.completedEnd ?? null,
      } as never);


      if (error) {
        logger.error('Activities', 'list_user_activities_paginated falhou', error);
        throw error;
      }

      const rows = (data ?? []) as ActivityRow[];
      // total_count vem replicado em todas as linhas (COUNT(*) OVER())
      const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      return {
        rows,
        total,
        page,
        pageSize,
        totalPages,
        hasPrev: page > 1,
        hasNext: page < totalPages,
      };
    },
    staleTime: 30_000, // 30s — listas de atividades mudam com frequência moderada
  });
}

// =============================================================================
// useActivitiesKpis — KPIs do mesmo conjunto filtrado
// =============================================================================

export interface UseActivitiesKpisParams extends ActivitiesFilters {
  enabled?: boolean;
}

/**
 * KPIs do período: pendentes hoje, atrasadas, concluídas no período,
 * total no período e taxa de conclusão (%).
 *
 * Aceita os mesmos filtros do listador (exceto paginação/ordenação) para
 * garantir coerência entre cards e tabela.
 */
export function useActivitiesKpis(params: UseActivitiesKpisParams = {}) {
  const { timezone = 'America/Sao_Paulo', enabled = true, ...filters } = params;

  return useQuery({
    queryKey: ['activities-kpis', { timezone, ...filters }],
    enabled,
    queryFn: async (): Promise<ActivitiesKpis> => {
      // v1.1.0 — Inclui workspace/category/product/source para alinhar com a Lista
      const { data, error } = await supabase.rpc('get_activities_kpis', {
        p_date_start: filters.dateStart ?? null,
        p_date_end: filters.dateEnd ?? null,
        p_assignee_ids: filters.assigneeIds ?? null,
        p_activity_types: filters.activityTypes ?? null,
        p_pipeline_ids: filters.pipelineIds ?? null,
        p_phase_ids: filters.phaseIds ?? null,
        p_playbook_ids: filters.playbookIds ?? null,
        p_origin: filters.origin ?? null,
        p_search: filters.search ?? null,
        p_timezone: timezone,
        p_workspace_ids: filters.workspaceIds ?? null,
        p_category_ids: filters.categoryIds ?? null,
        p_product_ids: filters.productIds ?? null,
        p_sources: filters.sources ?? null,
        // v1.4.0 — filtro por status do negócio
        p_deal_status: filters.dealStatus ?? null,
        // v1.7.0 — filtro "Realizado por" propagado para os KPIs
        p_performed_by_ids: filters.performedByIds ?? null,
      } as never);

      if (error) {
        logger.error('Activities', 'get_activities_kpis falhou', error);
        throw error;
      }

      // RPC retorna 1 linha — normaliza para objeto com defaults
      const row = (data?.[0] ?? {}) as Partial<ActivitiesKpis>;
      return {
        pending_today: Number(row.pending_today ?? 0),
        overdue: Number(row.overdue ?? 0),
        completed_in_period: Number(row.completed_in_period ?? 0),
        skipped_in_period: Number(row.skipped_in_period ?? 0),
        total_in_period: Number(row.total_in_period ?? 0),
        pending_in_period: Number(row.pending_in_period ?? 0),
        completion_rate: Number(row.completion_rate ?? 0),
      };
    },
    staleTime: 30_000,
  });
}

// =============================================================================
// useActivitiesTypeCounts — contadores agrupados por tipo de atividade
// =============================================================================

/** Linha retornada pela RPC `get_activities_type_counts`. */
export interface ActivityTypeCountRow {
  activity_type: string;
  total_count: number;
}

export interface UseActivitiesTypeCountsParams extends ActivitiesFilters {
  enabled?: boolean;
}

/**
 * Contadores por tipo de atividade respeitando os mesmos filtros da listagem.
 * Retorna um objeto `{ [activity_type]: count }` mais a chave `'all'`.
 */
export function useActivitiesTypeCounts(params: UseActivitiesTypeCountsParams = {}) {
  const { timezone = 'America/Sao_Paulo', enabled = true, ...filters } = params;

  return useQuery({
    queryKey: ['activities-type-counts', { timezone, ...filters }],
    enabled,
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase.rpc('get_activities_type_counts' as never, {
        p_date_start: filters.dateStart ?? null,
        p_date_end: filters.dateEnd ?? null,
        p_assignee_ids: filters.assigneeIds ?? null,
        p_status: filters.status ?? null,
        p_pipeline_ids: filters.pipelineIds ?? null,
        p_phase_ids: filters.phaseIds ?? null,
        p_playbook_ids: filters.playbookIds ?? null,
        p_origin: filters.origin ?? null,
        p_search: filters.search ?? null,
        p_workspace_ids: filters.workspaceIds ?? null,
        p_category_ids: filters.categoryIds ?? null,
        p_product_ids: filters.productIds ?? null,
        p_sources: filters.sources ?? null,
        p_timezone: timezone,
        // v1.1.0 — restringe contagem aos tipos visíveis da Lista (call/email/whatsapp/meeting/task)
        // Garante que a aba "Todos" coincida com KPI Total no Período e com a soma das demais abas.
        p_activity_types: filters.activityTypes ?? null,
        // v1.6.0 — filtro "Realizado por" propagado para os contadores por tipo
        p_performed_by_ids: filters.performedByIds ?? null,
      } as never);

      if (error) {
        logger.error('Activities', 'get_activities_type_counts falhou', error);
        throw error;
      }

      const rows = (data ?? []) as ActivityTypeCountRow[];
      const out: Record<string, number> = {};
      for (const r of rows) {
        out[r.activity_type] = Number(r.total_count) || 0;
      }
      return out;
    },
    staleTime: 30_000,
  });
}
